import type { FC } from "react";
import {
  BOARD_ICON_HEIGHT,
  DIMENSION_COLOR,
  GROUND_HATCH_COLOR,
  GROUND_HATCH_ID,
  JOIST_ICON_HEIGHT,
  LABEL_COLOR,
  MIN_VISIBLE_HEIGHT_RATIO,
  TOTAL_HEIGHT_COLOR,
  WARNING_COLOR,
} from "./deckSlopeViewConstants";
import HorizontalDimension from "./HorizontalDimension";
import JoistWithBoardsIcon from "./JoistWithBoardsIcon";
import VerticalDimension from "./VerticalDimension";
import type { SectionPreviewSize } from "./ZoomableSectionPreview";

type SlopeSectionProps = {
  blockIndex: number;
  blockHeight: number;
  title: string;
  // The side's length, already formatted (with its unit).
  lengthText: string;
  runLabel: string;
  slopeDegrees: number;
  nearHeightMm: number;
  farHeightMm: number;
  // Which end is the reference corner (terrain rise 0, where H is measured from) - H is only
  // dimensioned there, since everywhere else the ground sits higher than H's own base.
  totalHeightSide: "near" | "far" | null;
  totalHeightMm: number;
  previewSize: SectionPreviewSize;
  unitDeg: string;
  // Whole millimeters, with their unit.
  formatMm: (valueMm: number) => string;
  unreachableLabel: string;
};

// One side elevation: the deck (joist + boards icon) stays flat, while the ground beneath it follows
// the terrain plane from the side's start corner (near, left) to its end corner (far, right) - so the
// support height h changes along the side. Deliberately not to scale; dimensioned like an
// architectural section instead of floating numbers.
const SlopeSection: FC<SlopeSectionProps> = ({
  blockIndex,
  blockHeight,
  title,
  lengthText,
  runLabel,
  slopeDegrees,
  nearHeightMm,
  farHeightMm,
  totalHeightSide,
  totalHeightMm,
  previewSize,
  unitDeg,
  formatMm,
  unreachableLabel,
}) => {
  const scale = previewSize.width / 760;
  const isNearReachable = nearHeightMm >= 0;
  const isFarReachable = farHeightMm >= 0;

  const blockTop = blockIndex * blockHeight;
  const titleFontSize = 16 * scale;
  const dimensionFontSize = 12 * scale;
  const dimensionStrokeWidth = Math.max(1.2 * scale, 1);
  const tickSizePx = 9 * scale;

  const titleY = blockTop + blockHeight * 0.1;
  const deckY = blockTop + blockHeight * 0.32;
  const totalHeightTopY = deckY - (JOIST_ICON_HEIGHT + BOARD_ICON_HEIGHT) * scale;
  const groundBottom = blockTop + blockHeight * 0.62;
  const groundHeight = groundBottom - deckY;
  const left = previewSize.width * (270 / 760);
  const right = previewSize.width - previewSize.width * (170 / 760);
  const innerDimOffset = 55 * scale;
  const outerDimOffset = 130 * scale;
  const labelGap = 8 * scale;
  const horizontalDimY = groundBottom + blockHeight * 0.14;

  // The taller of the two heights reaches the frame's bottom edge; the other is drawn at its real
  // proportion of that, so the two are comparable even though the drawing overall isn't to scale.
  const maxHeightMm = Math.max(nearHeightMm, farHeightMm, 1);
  const heightToRatio = (heightMm: number): number =>
    Math.max(Math.max(heightMm, 0) / maxHeightMm, MIN_VISIBLE_HEIGHT_RATIO);
  const nearGroundY = deckY + groundHeight * heightToRatio(nearHeightMm);
  const farGroundY = deckY + groundHeight * heightToRatio(farHeightMm);

  const nearLabel = isNearReachable ? `${formatMm(nearHeightMm)} (h)` : unreachableLabel;
  const farLabel = isFarReachable ? `${formatMm(farHeightMm)} (h)` : unreachableLabel;
  const totalHeightLabel = `${formatMm(totalHeightMm)} (H)`;

  return (
    <g>
      <text
        x={previewSize.width / 2}
        y={titleY}
        textAnchor="middle"
        fontSize={titleFontSize}
        fontWeight={700}
        fill={LABEL_COLOR}
      >
        {title} — {Math.round(slopeDegrees * 100) / 100} {unitDeg}
      </text>

      <polygon
        points={`${left},${nearGroundY} ${right},${farGroundY} ${right},${groundBottom} ${left},${groundBottom}`}
        fill={`url(#${GROUND_HATCH_ID})`}
      />
      <line
        x1={left}
        y1={nearGroundY}
        x2={right}
        y2={farGroundY}
        stroke={GROUND_HATCH_COLOR}
        strokeWidth={Math.max(2 * scale, 1.2)}
      />

      <JoistWithBoardsIcon left={left} right={right} joistBottomY={deckY} scale={scale} />

      {totalHeightSide === "near" && (
        <VerticalDimension
          x={left}
          yTop={totalHeightTopY}
          yBottom={nearGroundY}
          dimX={left - outerDimOffset}
          label={totalHeightLabel}
          labelX={left - outerDimOffset - labelGap}
          labelAnchor="end"
          color={TOTAL_HEIGHT_COLOR}
          fontWeight={700}
          strokeWidth={dimensionStrokeWidth}
          tickSizePx={tickSizePx}
          fontSize={dimensionFontSize}
        />
      )}
      {totalHeightSide === "far" && (
        <VerticalDimension
          x={right}
          yTop={totalHeightTopY}
          yBottom={farGroundY}
          dimX={right + outerDimOffset}
          label={totalHeightLabel}
          labelX={right + outerDimOffset + labelGap}
          labelAnchor="start"
          color={TOTAL_HEIGHT_COLOR}
          fontWeight={700}
          strokeWidth={dimensionStrokeWidth}
          tickSizePx={tickSizePx}
          fontSize={dimensionFontSize}
        />
      )}
      <VerticalDimension
        x={left}
        yTop={deckY}
        yBottom={nearGroundY}
        dimX={left - innerDimOffset}
        label={nearLabel}
        labelX={left - (outerDimOffset + innerDimOffset) / 2}
        labelAnchor="middle"
        color={isNearReachable ? DIMENSION_COLOR : WARNING_COLOR}
        fontWeight={isNearReachable ? 600 : 700}
        strokeWidth={dimensionStrokeWidth}
        tickSizePx={tickSizePx}
        fontSize={dimensionFontSize}
      />
      <VerticalDimension
        x={right}
        yTop={deckY}
        yBottom={farGroundY}
        dimX={right + innerDimOffset}
        label={farLabel}
        labelX={right + innerDimOffset + labelGap}
        labelAnchor="start"
        color={isFarReachable ? DIMENSION_COLOR : WARNING_COLOR}
        fontWeight={isFarReachable ? 600 : 700}
        strokeWidth={dimensionStrokeWidth}
        tickSizePx={tickSizePx}
        fontSize={dimensionFontSize}
      />
      <HorizontalDimension
        xLeft={left}
        xRight={right}
        y={groundBottom}
        dimY={horizontalDimY}
        label={runLabel ? `${runLabel} = ${lengthText}` : lengthText}
        strokeWidth={dimensionStrokeWidth}
        tickSizePx={tickSizePx}
        fontSize={dimensionFontSize}
      />
    </g>
  );
};

export default SlopeSection;
